import * as React from 'react';
import { Box, Button, Fade, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { Scrollbar } from '@europrocurement/flexy-components';
import { faCircleInfo } from '@fortawesome/sharp-regular-svg-icons';
import AnimatedCounter from '../AnimatedCounter/AnimatedCounter';
import { useWidgetColors } from './useWidgetColors';
import { type WidgetLayoutProps } from './WidgetLayoutProps';

const WidgetLayout: React.FunctionComponent<WidgetLayoutProps> = function ({
    icon,
    title,
    data,
    dataError,
    dataTimedOut,
    onRetryData,
    actionMessage,
    loadingMessage,
    isLoading,
    onActionClick,
    color,
    paragraph,
    hasSpace,
}) {
    const [showParagraph, setShowParagraph] = React.useState(false);
    const {
        colorMain,
        colorDark,
        iconBg,
        iconActiveBg,
        buttonBgFlat,
        buttonTextColor,
        bgPaper,
        actionButtonSx,
    } = useWidgetColors(color);

    // When paragraph is shown without enough space, it replaces the counter
    const showCounter = !showParagraph || hasSpace;

    let paragraphAriaLabel: string | undefined;
    if (paragraph) {
        paragraphAriaLabel = showParagraph ? 'Masquer la description' : 'Afficher la description';
    }

    return (
        <>
            {/* Main content area — fills available height above button */}
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    width: '100%',
                    alignItems: 'start',
                    justifyContent: 'space-between',
                    position: 'relative',
                    overflow: 'hidden',
                    mb: 0.5,
                    // Fade toward the button below
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: '-5px',
                        left: 0,
                        right: 0,
                        height: 0,
                        boxShadow: `0 0 10px 14px ${bgPaper}`,
                        pointerEvents: 'none',
                        zIndex: 1,
                    },
                }}
            >
                {/* Icon + Title */}
                <Box
                    sx={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.5,
                        mb: 1,
                    }}
                >
                    <Box
                        role={paragraph ? 'button' : undefined}
                        aria-label={paragraphAriaLabel}
                        onClick={paragraph ? () => setShowParagraph((v) => !v) : undefined}
                        sx={{
                            position: 'relative',
                            width: 56,
                            height: 56,
                            borderRadius: '10px',
                            ...(showParagraph ? iconActiveBg : iconBg),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            lineHeight: 0,
                            paddingRight: '8px',
                            paddingBottom: '2px',
                            cursor: paragraph ? 'pointer' : 'default',
                            transition: 'background 0.2s ease',
                            '& > *': { display: 'block' },
                            ...(paragraph && { '&:hover': { ...iconActiveBg } }),
                        }}
                    >
                        <FaOptionIcon
                            {...icon.props}
                            size="lg"
                            color={colorMain}
                            optionColor={colorDark}
                            iconProps={{ ...icon.props.iconProps }}
                        />
                        {paragraph && !showParagraph && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    bottom: -8,
                                    right: -8,
                                    color: colorMain,
                                    backgroundColor: bgPaper,
                                    borderRadius: '50%',
                                    lineHeight: 0,
                                    padding: '1px',
                                    '@keyframes hintBounce': {
                                        '0%': { transform: 'translateY(0)', opacity: 1 },
                                        '12%': { transform: 'translateY(-5px)' },
                                        '24%': { transform: 'translateY(0)' },
                                        '36%': { transform: 'translateY(-4px)' },
                                        '48%': { transform: 'translateY(0)' },
                                        '60%': { transform: 'translateY(-2px)' },
                                        '72%': { transform: 'translateY(0)' },
                                        '100%': { transform: 'translateY(0)', opacity: 0.45 },
                                    },
                                    animation: 'hintBounce 2.2s ease forwards',
                                }}
                            >
                                <FontAwesomeIcon
                                    icon={faCircleInfo}
                                    size="sm"
                                />
                            </Box>
                        )}
                    </Box>
                    <Box
                        sx={{
                            position: 'relative',
                            flex: 1,
                            minWidth: 0,
                            height: 80,
                            width: '100%',
                            overflow: 'hidden',
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                bottom: '-5px',
                                left: 0,
                                right: 0,
                                height: 0,
                                boxShadow: `0 0 14px 20px ${bgPaper}`,
                                pointerEvents: 'none',
                                zIndex: 1,
                            },
                        }}
                    >
                        <Scrollbar style={{ height: '100%' }}>
                            <Typography
                                sx={{
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    color: 'text.primary',
                                    lineHeight: 1.4,
                                }}
                            >
                                {title}
                            </Typography>
                        </Scrollbar>
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        width: '100%',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mt: '-10px',
                    }}
                >
                    {/* Paragraph — shown when toggled */}
                    {paragraph && (
                        <Fade
                            in={showParagraph}
                            unmountOnExit
                            timeout={{ enter: 220, exit: 160 }}
                        >
                            <Box
                                sx={{
                                    height: '100%',
                                    width: '100%',
                                    position: 'relative',
                                    ...iconBg,
                                    borderRadius: '8px',
                                    padding: '10px 14px',
                                    overflow: 'hidden',
                                    mb: 1,
                                    '@keyframes bubbleSlideIn': {
                                        from: { opacity: 0, transform: 'translateY(-6px)' },
                                        to: { opacity: 1, transform: 'translateY(0)' },
                                    },
                                    animation: 'bubbleSlideIn 0.22s ease forwards',
                                    '&::after': {
                                        content: '""',
                                        position: 'absolute',
                                        bottom: 0,
                                        left: 0,
                                        right: 0,
                                        height: 0,
                                        boxShadow: `0 0 16px 12px ${buttonBgFlat}`,
                                        pointerEvents: 'none',
                                    },
                                }}
                            >
                                <Scrollbar>
                                    <Typography
                                        sx={{
                                            fontSize: '13px',
                                            color: buttonTextColor,
                                            lineHeight: 1.6,
                                        }}
                                    >
                                        {paragraph}
                                    </Typography>
                                </Scrollbar>
                            </Box>
                        </Fade>
                    )}

                    {/* Counter — pushed to bottom, hidden when paragraph replaces it */}
                    {showCounter && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '100%',
                                height: '100%',
                            }}
                        >
                            <AnimatedCounter
                                value={data}
                                error={dataError || dataTimedOut}
                                onRetry={onRetryData}
                                sx={{
                                    display: 'block',
                                    fontSize: '26px',
                                    fontWeight: 600,
                                    lineHeight: 1.2,
                                    color: 'text.primary',
                                    whiteSpace: 'nowrap',
                                }}
                            />
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Button — always at bottom */}
            <Box sx={{ flexShrink: 0, mt: -0.5 }}>
                <Button
                    disabled={isLoading}
                    onClick={onActionClick}
                    sx={{ ...actionButtonSx, width: '100%', padding: '8px 20px' }}
                >
                    {isLoading ? loadingMessage : actionMessage}
                </Button>
            </Box>
        </>
    );
};

export default WidgetLayout;
